import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getSupabase } from '../lib/supabase.js';

// D-15 (owner design 2026-08-22): the red sibling of PendingDrafts. A
// schedule fired an EXPECTED charge; this card asks the payer to attest it
// actually happened. Deliberately rose/red end to end — the TK-C draft card
// is amber and evidence-backed (the card API saw a real transaction); this
// one is EXPECTATION-backed, and the owner asked for a color a distracted
// investor cannot blind-click ("make it in red or something").
//
// Actions (COST_MODULE_REDESIGN_PLAN.md §2):
//   Confirm charge   → books at the suggested amount (the TK-C precedent)
//   Didn't happen    → records a skip; nothing books
//   Edit amount      → inline field; for an investor an edited figure goes
//                      PENDING to Kareem (no longer the approved amount) —
//                      the button says so before they commit.
//
// Mounts: investor Home (desktop + mobile top) with their own investor-paid
// suggestions; AdminOverview (variant="admin") with the kareem-paid ones —
// the payer is the one who attests.
//
// T-055 (2026-10-20): the investor variant takes the viewed household
// (member + members, the same pair CostLinesCard gets) and keeps only its
// rows. RLS already scopes a real investor, but the admin's RLS sees every
// household — acting as Beko, Kareem was shown Kareem's own and Badr's
// cards as if they were Beko's, while Beko's own login (correctly) showed
// nothing. In a family with more than one member each row also names whose
// subscription it is, since two "Coinbase One" rows look the same otherwise.

const S = {
  card: { background: 'var(--surface,#fff)', border: '1px solid #fecaca', borderRadius: 16, padding: '14px 16px', margin: '0 0 16px', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' },
  head: { fontSize: 13, fontWeight: 800, color: '#b91c1c', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 },
  sub: { fontSize: 12, color: 'var(--text-2,#475569)', marginBottom: 8 },
  row: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderTop: '1px solid #fee2e2', flexWrap: 'wrap' },
  name: { fontSize: 13.5, fontWeight: 800, color: 'var(--text-1,#0f172a)' },
  meta: { fontSize: 12, color: 'var(--text-3,#64748b)' },
  amt: { fontSize: 16, fontWeight: 800, color: '#b91c1c', fontVariantNumeric: 'tabular-nums' },
  actions: { display: 'flex', gap: 6, marginLeft: 'auto', alignItems: 'center', flexWrap: 'wrap' },
  confirm: { fontSize: 12.5, fontWeight: 700, padding: '7px 12px', borderRadius: 9, border: '1px solid #b91c1c', background: '#b91c1c', color: '#fff', cursor: 'pointer' },
  ghost: { fontSize: 12.5, fontWeight: 600, padding: '7px 11px', borderRadius: 9, border: '1px solid var(--border,#e2e8f0)', background: 'var(--surface,#fff)', color: 'var(--text-2,#334155)', cursor: 'pointer' },
  input: { width: 92, padding: '6px 8px', borderRadius: 8, border: '1px solid #fecaca', fontSize: 13, fontVariantNumeric: 'tabular-nums' },
  err: { fontSize: 12, color: '#b91c1c', marginTop: 6 },
};

const fmtDue = (d) => {
  try { return new Date(d + 'T00:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }); }
  catch { return d; }
};

export default function CostSuggestionsCard({ variant = 'investor', member = null, members = null }) {
  const [rows, setRows] = useState(null);
  const [names, setNames] = useState({});
  const [editing, setEditing] = useState(null);   // { id, value }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // The viewed household: the holder plus everyone they manage. Empty when
  // the mount gives no household (admin variant) — then nothing is filtered.
  const familyIds = useMemo(
    () => [...new Set([member?.id, ...(members || []).map((m) => m.id)].filter(Boolean))],
    [member?.id, members],
  );
  const familyKey = familyIds.join('|');
  const familyNames = useMemo(
    () => Object.fromEntries((members || []).map((m) => [m.id, m.name || m.display_name])),
    [members],
  );

  const load = useCallback(async () => {
    const sb = getSupabase();
    if (!sb) { setRows([]); return; }
    // RLS scopes items to the viewer's household (admin sees all). The payer
    // attests: investors see their investor-paid suggestions, the admin
    // variant shows the kareem-paid ones.
    let q = sb.from('investment_cost_items')
      .select('id, member_id, amount, note, paid_by, due_date, month')
      .eq('status', 'suggested')
      .order('due_date', { ascending: true });
    q = variant === 'admin' ? q.eq('paid_by', 'kareem') : q.eq('paid_by', 'investor');
    const { data, error: err } = await q;
    if (err) { setRows([]); return; }
    // T-055: an admin acting as one household must see that household only.
    const family = new Set(familyKey ? familyKey.split('|') : []);
    const scoped = variant === 'investor' && family.size
      ? (data || []).filter((r) => family.has(r.member_id))
      : (data || []);
    setRows(scoped);
    if (variant === 'admin' && scoped.length) {
      const ids = [...new Set(scoped.map((r) => r.member_id))];
      const { data: mems } = await sb.from('members').select('id, name, display_name').in('id', ids);
      setNames(Object.fromEntries((mems || []).map((m) => [m.id, m.display_name || m.name])));
    }
  }, [variant, familyKey]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn) => {
    setBusy(true); setError(null);
    try { await fn(); setEditing(null); await load(); window.dispatchEvent(new CustomEvent('finops:enrollment-changed')); }
    catch (e) { setError(e?.message || String(e)); }
    finally { setBusy(false); }
  };

  const confirm = (r, amount) => act(async () => {
    const sb = getSupabase();
    const { error: err } = await sb.rpc('v2_confirm_cost_occurrence', {
      p_item_id: r.id, p_amount: amount ?? null,
    });
    if (err) throw err;
  });

  const skip = (r) => act(async () => {
    const sb = getSupabase();
    const { error: err } = await sb.rpc('v2_skip_cost_occurrence', { p_item_id: r.id, p_note: null });
    if (err) throw err;
  });

  if (!rows || rows.length === 0) return null;

  return (
    <section className="cost-suggestions-card" style={S.card} aria-label="Expected costs awaiting confirmation">
      <div style={S.head}>⛔ Expected cost{rows.length === 1 ? '' : 's'} — confirm what actually happened</div>
      <div style={S.sub}>
        These are <strong>predicted from a schedule, not observed charges</strong>. Nothing counts until you answer.
      </div>
      {error && <div style={S.err}>{error}</div>}
      {rows.map((r) => (
        <div key={r.id} className="cost-suggestion" data-member={r.member_id} style={S.row}>
          <div>
            <div style={S.name}>
              {variant === 'admin' && <span style={{ color: 'var(--text-3,#94a3b8)', fontWeight: 600 }}>{names[r.member_id] || r.member_id} · </span>}
              {variant !== 'admin' && familyIds.length > 1 && familyNames[r.member_id] && (
                <span className="cost-suggestion-who" style={{ color: 'var(--text-3,#94a3b8)', fontWeight: 600 }}>{familyNames[r.member_id]} · </span>
              )}
              {r.note}
            </div>
            <div style={S.meta}>due {fmtDue(r.due_date)}{r.paid_by === 'kareem' ? ' · your money' : ' · your money, your call'}</div>
          </div>
          <div style={S.amt}>${Number(r.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
          {editing && editing.id === r.id ? (
            <div style={S.actions}>
              <input type="number" min="0.01" step="0.01" autoFocus value={editing.value} disabled={busy}
                onChange={(e) => setEditing({ id: r.id, value: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Escape') setEditing(null); }}
                style={S.input} />
              <button type="button" style={S.confirm} disabled={busy}
                onClick={() => confirm(r, Number(editing.value))}>
                {variant === 'admin' ? 'Book edited' : 'Send edited → Kareem'}
              </button>
              <button type="button" style={S.ghost} disabled={busy} onClick={() => setEditing(null)}>Cancel</button>
            </div>
          ) : (
            <div style={S.actions}>
              <button type="button" style={S.confirm} disabled={busy} onClick={() => confirm(r, null)}>Confirm charge</button>
              <button type="button" style={S.ghost} disabled={busy} onClick={() => skip(r)}>Didn't happen</button>
              <button type="button" style={S.ghost} disabled={busy} onClick={() => setEditing({ id: r.id, value: String(r.amount) })}>Edit amount</button>
            </div>
          )}
        </div>
      ))}
    </section>
  );
}
